import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowUp, Check, Clipboard, Play, icons } from "lucide-react";

import { getFeed, getFeedViews } from "../../../../api/feed";
import { useCompanyContext } from "../../../../context";
import { formatPostDate, getPostMillis } from "../../../../utils/postDate";
import { feedViewIcon, isFontAwesomeIcon } from "../../../../utils/viewIcons";
import ArticleDetailPanel from "../../../Universal/ArticleCardDetailPanel";
import LoadingSpinner from "../../../Universal/LoadingSpinner";
import { ScrollSentinel } from "../../Feeds/FeedCard";
import { useDark } from "../../Feeds/feedTheme";
import { useRightSidebar } from "../../../contexts/RightSidebarContext";

const cyan = "14,165,233";

const normalizeSourceValue = (value) => {
  if (!value) return "";

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
};

/* -------------------------------------------------------------------------- */
/*                                 Feed views                                 */
/* -------------------------------------------------------------------------- */

// The default view is "everything" and is never rendered as a chip.
const DEFAULT_VIEW_KEY = "Global";

// Loose aliases for item fields that don't use canonical target_type names.
const TARGET_TYPE_ALIASES = {
  commercials: "commercial",
  ispot: "commercial",
  yt: "youtube",
  x: "twitter",
  tweet: "twitter",
};

const canonicalTargetType = (value) => {
  const normalized = normalizeSourceValue(value);
  return TARGET_TYPE_ALIASES[normalized] || normalized;
};

const isDefaultView = (view) =>
  normalizeSourceValue(view?.key) === normalizeSourceValue(DEFAULT_VIEW_KEY);

const getFeedViewsPayload = (response) => response?.data ?? response ?? {};

const getSystemViews = (response) => {
  const payload = getFeedViewsPayload(response);
  return Array.isArray(payload.system_views) ? payload.system_views : [];
};

const getRawTargetTypes = (view) =>
  Array.isArray(view?.target_types) ? view.target_types.filter(Boolean) : [];

const toFilterView = (view) => {
  const rawTargetTypes = getRawTargetTypes(view);

  return {
    id: String(view.key),
    label: view.label || String(view.key),
    icon: view.icon,
    // Backend color: an rgba() string or a CSS gradient (see GradientDefs).
    color: view.color || `rgba(${cyan},0.85)`,
    // Raw strings go to the API; canonical ones are for matching items.
    rawTargetTypes,
    targetTypes: new Set(rawTargetTypes.map(canonicalTargetType)),
  };
};

// Every system view except the default becomes a filter chip (deduped by key).
const buildFilterViews = (systemViews) => {
  const seen = new Set();

  return systemViews
    .filter((view) => {
      if (!view?.key || isDefaultView(view)) return false;

      const id = String(view.key);
      if (seen.has(id)) return false;

      seen.add(id);
      return true;
    })
    .map(toFilterView);
};

// What we ask the API for: the union of the ENABLED chip views' target types.
// If there are no chips at all, fall back to the default view's target types.
const buildFetchTargetTypes = (systemViews, filterViews, enabledViews) => {
  const types = filterViews.length
    ? filterViews
        .filter((view) => enabledViews[view.id] !== false)
        .flatMap((view) => view.rawTargetTypes)
    : getRawTargetTypes(systemViews.find(isDefaultView));

  return [...new Set(types)];
};

// Every target type any view knows about; used to resolve an item's type.
const buildKnownTargetTypes = (systemViews) =>
  new Set(
    systemViews
      .flatMap(getRawTargetTypes)
      .map(canonicalTargetType)
      .filter(Boolean),
  );

const isViewEnabled = (enabledViews, viewId) => enabledViews[viewId] !== false;

const withColorAlpha = (color, alpha, fallback = `rgba(${cyan},${alpha})`) => {
  if (!color) return fallback;

  const match = String(color).match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return fallback;

  const parts = match[1].split(",").map((part) => part.trim());
  if (parts.length < 3) return fallback;

  const [red, green, blue] = parts;
  return `rgba(${red},${green},${blue},${alpha})`;
};

const NODE_COLLISION_GAP = 10;

// Scroll distance before the back-to-top button appears
const BACK_TO_TOP_THRESHOLD = 600;

const toBool = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
};

// published_at → date → timestamp, in any shape the API sends (see utils/postDate)
const getTimestamp = getPostMillis;

// MM-DD-YYYY, same as the detail panel
const formatTimelineDay = (item) => formatPostDate(item) || "No date";

const getBestUrl = (item) => item.url || item.title || "";

// Resolve an item to a canonical target type the feed views know about.
const getItemTargetType = (item, knownTargetTypes) => {
  const candidates = [
    item.target_type,
    item.targetType,
    item.source,
    item.source_type,
    item.sourceType,
    item.platform,
    item.endpoint,
    item.type,
  ];

  for (const candidate of candidates) {
    const targetType = canonicalTargetType(candidate);
    if (targetType && knownTargetTypes.has(targetType)) return targetType;
  }

  const url = String(item.url || item.title || "").toLowerCase();

  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  if (url.includes("ispot.tv")) return "commercial";
  if (url.includes("twitter.com") || /(?:^|[/.])x\.com(?:\/|$)/.test(url)) {
    return "twitter";
  }

  return "";
};

const normalizeContent = (content) => {
  if (!content) return "";

  return String(content)
    .replace(/\s+/g, " ")
    .replace(/\s+\./g, ".")
    .replace(/\s+,/g, ",")
    .trim();
};

const cleanTitle = (item) => {
  const title = String(item.title || "").trim();

  if (!title) return "Untitled";

  return title
    .replace(/^Thumbnail for\s+/i, "")
    .replace(/\s+-\s+iSpot\s*$/i, "")
    .trim();
};

const buildPayload = ({ offset, target, targetTypes }) => {
  const payload = {
    offset,
    group: "public",
    sort: -1,
  };

  if (targetTypes?.length) payload.target_types = targetTypes;
  if (target) payload.target = target;

  return payload;
};

const TimelineMarker = ({ item, view, side, markerOffset = 0, markerRef }) => {
  const isLeft = side === "left";
  const day = formatTimelineDay(item);
  const connectorTop = 34 + markerOffset;
  const markerTop = 12 + markerOffset;

  return (
    <>
      <div
        className={`pointer-events-none absolute z-10 hidden h-px lg:block ${
          isLeft ? "left-full" : "right-full"
        }`}
        style={{
          top: connectorTop,
          width: 68,
          background: `rgba(${cyan},0.14)`,
        }}
      />

      <div
        ref={markerRef}
        data-node-offset={markerOffset}
        className={`pointer-events-none absolute z-20 hidden min-h-[44px] min-w-[72px] flex-col items-center justify-center rounded-2xl border bg-mainBg px-3 py-2 text-center shadow-login-panel lg:flex ${
          isLeft ? "left-[calc(100%+68px)]" : "right-[calc(100%+68px)]"
        }`}
        style={{
          top: markerTop,
          transform: isLeft ? "translateX(-50%)" : "translateX(50%)",
          borderColor: `rgba(${cyan},0.16)`,
          boxShadow: `
            0 0 0 1px rgba(${cyan},0.04),
            0 0 16px rgba(${cyan},0.045)
          `,
        }}
      >
        {view && (
          <FeedViewIcon
            icon={view.icon}
            color={view.color}
            className="mb-1 h-3.5 w-3.5 shrink-0"
          />
        )}
        <span className="block whitespace-nowrap text-[10px] font-normal leading-tight text-mainText/85">
          {day}
        </span>
      </div>
    </>
  );
};

const getPreviewImage = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const ScreenshotPreview = ({ item }) => {
  const videoScreenshot = getPreviewImage(item.video_screenshot);
  const fallbackScreenshot = getPreviewImage(item.screenshot);
  const hasVideo = Boolean(item.video);
  const [activeImage, setActiveImage] = useState(
    videoScreenshot || fallbackScreenshot,
  );
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    setActiveImage(videoScreenshot || fallbackScreenshot);
    setPreviewFailed(false);
  }, [videoScreenshot, fallbackScreenshot]);

  const handlePreviewError = () => {
    if (activeImage === videoScreenshot && fallbackScreenshot) {
      setActiveImage(fallbackScreenshot);
      setPreviewFailed(false);
      return;
    }

    setPreviewFailed(true);
  };

  const showFallbackMessage = !activeImage || previewFailed;

  return (
    <div className="p-1.5">
      <div
        className="relative mx-auto overflow-hidden rounded-xl border bg-black"
        style={{
          aspectRatio: "1212 / 680",
          borderColor: `rgba(${cyan},0.16)`,
        }}
      >
        {activeImage && !previewFailed ? (
          <img
            key={activeImage}
            src={activeImage}
            alt=""
            className="h-full w-full object-cover object-center"
            loading="lazy"
            onError={handlePreviewError}
          />
        ) : null}

        {showFallbackMessage && (
          <div className="absolute inset-0 flex h-full w-full items-center justify-center p-6 text-center text-xs text-mutedText">
            No preview available
          </div>
        )}

        {hasVideo && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white shadow-login-panel backdrop-blur-md">
              <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CopyableUrl = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const [overflowPx, setOverflowPx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const viewportRef = useRef(null);
  const textRef = useRef(null);

  if (!url) return null;

  const measureOverflow = () => {
    const viewport = viewportRef.current;
    const text = textRef.current;

    if (!viewport || !text) return;

    const overflow = Math.max(0, text.scrollWidth - viewport.clientWidth);
    setOverflowPx(overflow);
  };

  const handleMouseEnter = () => {
    measureOverflow();
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error("[ABFeedTimelineCompact] Failed to copy URL:", err);
    }
  };

  const shouldScroll = isHovering && overflowPx > 0;
  const scrollDuration = Math.min(10, Math.max(2.5, overflowPx / 42));

  return (
    <button
      type="button"
      onClick={handleCopy}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={[
        "flex h-7 w-full min-w-0 items-center gap-1.5 rounded-lg border bg-innerBg",
        "px-2 text-left transition hover:bg-btnHover",
      ].join(" ")}
      style={{
        borderColor: `rgba(${cyan},0.12)`,
      }}
      title="Copy URL"
    >
      <span
        ref={viewportRef}
        className="flex h-full min-w-0 flex-1 items-center overflow-hidden"
      >
        <span
          ref={textRef}
          className="inline-block whitespace-nowrap text-[10px] leading-none text-mutedText transition-transform ease-linear"
          style={{
            transform: shouldScroll
              ? `translateX(-${overflowPx}px)`
              : "translateX(0)",
            transitionDuration: shouldScroll ? `${scrollDuration}s` : "180ms",
          }}
        >
          {url}
        </span>
      </span>

      <span className="flex h-full shrink-0 items-center justify-center">
        {copied ? (
          <Check className="h-3 w-3 text-velBlue" strokeWidth={2.2} />
        ) : (
          <Clipboard className="h-3 w-3 text-mutedText" strokeWidth={2.2} />
        )}
      </span>
    </button>
  );
};

// Icons come from the feed view's `icon` key (lucide names like "earth",
// "tv-minimal", "badge-cent", "x"). Accepts kebab, snake, spaced or PascalCase.
const toLucideName = (icon) => {
  const raw = String(icon || "").trim();
  if (!raw) return "";
  if (icons[raw]) return raw;

  return raw
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
};

const resolveLucideIcon = (icon) => icons[toLucideName(icon)] || icons.Circle;

/* ─── Gradient view colors ───
   A view color can be a CSS gradient (Instagram). SVG icons can't take a CSS
   gradient as `color`, so each gradient becomes an SVG <radialGradient> that
   the icon's stroke/fill points at via url(#id). Lucide icons are several
   paths on a fixed 24×24 box, so they get a userSpaceOnUse gradient; Font
   Awesome brand icons are one path, so objectBoundingBox is fine. */
const isGradientColor = (color) => /gradient\(/i.test(String(color || ""));

const isWhiteColor = (color) =>
  /^(#fff(fff)?|white|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*[\d.]+\s*)?\))$/i.test(
    String(color || "").trim(),
  );

const gradientId = (color) => {
  let hash = 0;
  for (const char of String(color)) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return `ab-view-grad-${(hash >>> 0).toString(36)}`;
};

const parseGradient = (color) => {
  const source = String(color);
  const at = source.match(/at\s+([\d.]+)%\s+([\d.]+)%/i);
  const stops = [
    ...source.matchAll(/(#[0-9a-f]{3,8}|rgba?\([^)]*\))\s+([\d.]+)%/gi),
  ].map((match) => ({ color: match[1], offset: Number(match[2]) / 100 }));

  return {
    cx: at ? Number(at[1]) / 100 : 0.5,
    cy: at ? Number(at[2]) / 100 : 0.5,
    stops,
  };
};

const GradientDefs = ({ colors }) => {
  const gradients = [...new Set(colors.filter(isGradientColor))];
  if (!gradients.length) return null;

  return (
    <svg aria-hidden width={0} height={0} className="absolute">
      <defs>
        {gradients.map((color) => {
          const id = gradientId(color);
          const { cx, cy, stops } = parseGradient(color);
          const stopNodes = stops.map((stop, index) => (
            <stop key={index} offset={stop.offset} stopColor={stop.color} />
          ));

          return [
            // Lucide: 24-unit box, radius ≈ farthest corner
            <radialGradient
              key={`${id}-l`}
              id={`${id}-l`}
              gradientUnits="userSpaceOnUse"
              cx={cx * 24}
              cy={cy * 24}
              r={30}
            >
              {stopNodes}
            </radialGradient>,
            // Font Awesome: single path, bounding-box units
            <radialGradient
              key={`${id}-f`}
              id={`${id}-f`}
              gradientUnits="objectBoundingBox"
              cx={cx}
              cy={cy}
              r={1.25}
            >
              {stopNodes}
            </radialGradient>,
          ];
        })}
      </defs>
    </svg>
  );
};

// `color` is the view color (rgba or gradient); `dimmed` fades it for an
// off state. White flips to near-black in light mode so X stays visible.
const FeedViewIcon = ({
  icon,
  color,
  dimmed = false,
  className = "h-3 w-3",
  style,
}) => {
  const dark = useDark();
  const isFontAwesome = isFontAwesomeIcon(icon);

  // Font Awesome names ("FaYoutube") load lazily through utils/viewIcons
  const Icon = isFontAwesome ? feedViewIcon(icon) : resolveLucideIcon(icon);

  let colorStyle = {};

  if (isGradientColor(color)) {
    const paint = `url(#${gradientId(color)}-${isFontAwesome ? "f" : "l"})`;
    colorStyle = isFontAwesome ? { fill: paint } : { stroke: paint };
    if (dimmed) colorStyle.opacity = 0.4;
  } else if (color) {
    const resolved = isWhiteColor(color) && !dark ? "rgba(15,23,42,1)" : color;
    colorStyle = { color: dimmed ? withColorAlpha(resolved, 0.4) : resolved };
  }

  return (
    <Icon
      className={className}
      style={{ ...colorStyle, ...style }}
      strokeWidth={1.8}
      aria-hidden
    />
  );
};

// One icon button per source view, centered above the timeline. The label
// lives in the tooltip; on/off is carried by the border glow and icon color.
const SourceFilters = ({ views, enabledViews, onToggleView, isLoading }) => {
  // Reserve the row's height while views load so nothing jumps.
  if (isLoading) return <div aria-hidden className="mb-4 h-11" />;

  // Only the default view exists: nothing to filter by.
  if (!views.length) return null;

  return (
    <div className="mb-4 flex min-w-0 flex-wrap items-center justify-center gap-2">
      {views.map((view) => {
        const isEnabled = isViewEnabled(enabledViews, view.id);

        return (
          <button
            key={view.id}
            type="button"
            aria-pressed={isEnabled}
            aria-label={`${view.label}: ${isEnabled ? "shown" : "hidden"}`}
            title={view.label}
            onClick={() => onToggleView(view.id)}
            className={[
              "flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg",
              "transition-all duration-200 ease-out outline-none",
              "focus-visible:ring-2 focus-visible:ring-inputFocus/60",
              isEnabled ? "" : "hover:bg-mainButtonBg/40",
            ].join(" ")}
            style={
              isEnabled
                ? {
                    border: `1px solid rgba(${cyan},0.38)`,
                    background: `radial-gradient(ellipse 80% 70% at 50% 0%, rgba(${cyan},0.06) 0%, rgba(${cyan},0.018) 58%, rgba(255,255,255,0.012) 100%)`,
                    animation: "ab-source-pulse 5s ease-in-out infinite",
                  }
                : {
                    border: `1px solid rgba(${cyan},0.08)`,
                    background: "rgba(255,255,255,0.012)",
                    boxShadow: `0 0 0 1px rgba(${cyan},0.01)`,
                  }
            }
          >
            <FeedViewIcon
              icon={view.icon}
              color={view.color}
              dimmed={!isEnabled}
              className="h-5 w-5 transition-colors duration-200"
            />
          </button>
        );
      })}
    </div>
  );
};

const TimelineSourceBadge = ({ source }) => {
  if (!source) return null;

  return (
    <div
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-mainBg/70 px-2 py-1"
      style={{
        borderColor: `rgba(${cyan},0.14)`,
        boxShadow: `inset 0 1px 0 rgba(${cyan},0.035), 0 0 8px rgba(${cyan},0.025)`,
      }}
    >
      <FeedViewIcon
        icon={source.icon}
        color={source.color || `rgba(${cyan},0.85)`}
        className="h-3 w-3 shrink-0"
      />
      <span className="min-w-0 truncate text-[10px] font-normal tracking-wide text-mutedText">
        {source.label}
      </span>
    </div>
  );
};

const CommercialTitle = ({ children }) => {
  if (!children) return null;

  return (
    <h3
      className="min-w-0 rounded-xl border bg-innerBg px-2.5 py-2 text-sm font-medium leading-5 text-mainText"
      style={{
        borderColor: `rgba(${cyan},0.22)`,
        boxShadow: `
          inset 0 1px 0 rgba(${cyan},0.045),
          0 0 0 1px rgba(${cyan},0.025)
        `,
      }}
    >
      <span className="line-clamp-2">{children}</span>
    </h3>
  );
};

const ExpandableContent = ({ content }) => {
  const [expanded, setExpanded] = useState(false);
  const text = normalizeContent(content);

  if (!text) return null;

  const shouldClamp = text.length > 220;

  return (
    <div className="rounded-xl border border-surfaceBorder bg-innerBg px-3 py-2">
      <p
        className={[
          "text-[11px] leading-5 text-mutedText",
          expanded ? "" : "line-clamp-4",
        ].join(" ")}
      >
        {text}
      </p>

      {shouldClamp && (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="mt-1.5 text-[10px] font-medium text-velBlue/80 transition hover:text-velBlue"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
};

const TimelineCard = ({ item, activeCardId, onCardClick, getItemView }) => {
  const isActive = activeCardId === item.document_id;
  const url = getBestUrl(item);
  const title = cleanTitle(item);
  const view = getItemView(item);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onCardClick(item)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onCardClick(item);
        }
      }}
      className={[
        "overflow-hidden rounded-2xl border bg-surface/80 shadow-login-panel backdrop-blur-md",
        "cursor-pointer outline-none transition hover:bg-surface/90",
        "focus-visible:ring-2 focus-visible:ring-inputFocus/60",
      ].join(" ")}
      style={{
        borderColor: isActive ? `rgba(${cyan},0.38)` : `rgba(${cyan},0.16)`,
        boxShadow: isActive
          ? `
            0 0 0 1px rgba(${cyan},0.08),
            0 0 16px rgba(${cyan},0.10),
            0 14px 45px rgba(0,0,0,0.24)
          `
          : `
            0 0 0 1px rgba(${cyan},0.035),
            0 14px 45px rgba(0,0,0,0.22)
          `,
      }}
    >
      <ScreenshotPreview item={item} />

      <div className="space-y-2 px-3 pb-3 pt-1.5">
        <TimelineSourceBadge source={view} />

        <CommercialTitle>{title}</CommercialTitle>

        <ExpandableContent content={item.content} />

        <CopyableUrl url={url} />
      </div>
    </article>
  );
};

const DesktopTimeline = ({ items, activeCardId, onCardClick, getItemView }) => {
  const markerRefs = useRef(new Map());
  const [markerOffsets, setMarkerOffsets] = useState({});

  const registerMarker = useCallback(
    (id) => (node) => {
      if (node) markerRefs.current.set(id, node);
      else markerRefs.current.delete(id);
    },
    [],
  );

  useLayoutEffect(() => {
    let raf = 0;

    const measure = () => {
      const nodes = items
        .map((item) => {
          const node = markerRefs.current.get(item.document_id);
          if (!node) return null;

          const rect = node.getBoundingClientRect();
          if (!rect.height || !rect.width) return null;

          const currentOffset = Number(node.dataset.nodeOffset || 0);

          return {
            id: item.document_id,
            baseTop: rect.top - currentOffset,
            height: rect.height,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.baseTop - b.baseTop);

      let previousBottom = -Infinity;
      const nextOffsets = {};

      nodes.forEach((node) => {
        const resolvedTop = Math.max(
          node.baseTop,
          previousBottom + NODE_COLLISION_GAP,
        );

        nextOffsets[node.id] = Math.max(
          0,
          Math.round(resolvedTop - node.baseTop),
        );

        previousBottom = resolvedTop + node.height;
      });

      setMarkerOffsets((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(nextOffsets);

        if (prevKeys.length !== nextKeys.length) return nextOffsets;

        const changed = nextKeys.some((key) => prev[key] !== nextOffsets[key]);
        return changed ? nextOffsets : prev;
      });
    };

    raf = window.requestAnimationFrame(measure);

    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  // Items pair up into rows (even index left, odd index right) on one shared
  // grid, so both sides always end on the same row. The right card of each row
  // sits a step lower (top-16) without adding to the row's height; the bottom
  // padding makes room for that step on the last row.
  return (
    <div className="relative hidden lg:grid lg:grid-cols-[minmax(0,1fr)_112px_minmax(0,1fr)] lg:items-start lg:gap-x-4 lg:gap-y-6 lg:pb-16">
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2"
        style={{
          background: `rgba(${cyan},0.13)`,
        }}
      />

      {items.map((item, index) => {
        const side = index % 2 === 0 ? "left" : "right";

        return (
          <div
            key={item.document_id}
            className={[
              "relative w-full min-w-0 max-w-[500px]",
              side === "left" ? "ml-auto" : "top-16 mr-auto",
            ].join(" ")}
            style={{
              gridRow: Math.floor(index / 2) + 1,
              gridColumn: side === "left" ? 1 : 3,
            }}
          >
            <TimelineMarker
              item={item}
              view={getItemView(item)}
              side={side}
              markerOffset={markerOffsets[item.document_id] || 0}
              markerRef={registerMarker(item.document_id)}
            />
            <TimelineCard
              item={item}
              activeCardId={activeCardId}
              onCardClick={onCardClick}
              getItemView={getItemView}
            />
          </div>
        );
      })}
    </div>
  );
};

const MobileTimeline = ({ items, activeCardId, onCardClick, getItemView }) => (
  <div className="space-y-4 lg:hidden">
    {items.map((item) => (
      <TimelineCard
        key={item.document_id}
        item={item}
        activeCardId={activeCardId}
        onCardClick={onCardClick}
        getItemView={getItemView}
      />
    ))}
  </div>
);

const ABFeedTimelineCompact = () => {
  const { currentCompany } = useCompanyContext();
  const { setIsRightOpen, setRightComponent, setRightSidebarMode, setRightId } =
    useRightSidebar();

  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [activeCardId, setActiveCardId] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const [systemViews, setSystemViews] = useState([]);
  const [viewsStatus, setViewsStatus] = useState("loading");
  // Keyed by view key. Missing = enabled, so every chip starts on.
  const [enabledViews, setEnabledViews] = useState({});

  const filterViews = useMemo(
    () => buildFilterViews(systemViews),
    [systemViews],
  );

  const knownTargetTypes = useMemo(
    () => buildKnownTargetTypes(systemViews),
    [systemViews],
  );

  // A string key keeps fetchPage stable unless the actual set of types changes,
  // so toggling a chip refetches exactly once.
  const fetchTargetTypesKey = useMemo(
    () =>
      buildFetchTargetTypes(systemViews, filterViews, enabledViews).join(","),
    [systemViews, filterViews, enabledViews],
  );

  const hasFilterViews = filterViews.length > 0;

  const enabledViewCount = useMemo(
    () =>
      filterViews.filter((view) => isViewEnabled(enabledViews, view.id)).length,
    [enabledViews, filterViews],
  );

  // The server filters by target_types now, so the client only drops
  // disliked items and sorts.
  const filteredItems = useMemo(
    () =>
      items
        .filter((item) => !toBool(item.dislike))
        .sort((a, b) => getTimestamp(b) - getTimestamp(a)),
    [items],
  );

  const getItemView = useCallback(
    (item) => {
      const targetType = getItemTargetType(item, knownTargetTypes);
      if (!targetType) return null;

      return (
        filterViews.find((view) => view.targetTypes.has(targetType)) || null
      );
    },
    [filterViews, knownTargetTypes],
  );

  const handleToggleView = useCallback((viewId) => {
    setEnabledViews((prev) => ({
      ...prev,
      [viewId]: !isViewEnabled(prev, viewId),
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadFeedViews = async () => {
      try {
        const response = await getFeedViews();
        if (!isMounted) return;

        setSystemViews(getSystemViews(response));
      } catch (err) {
        console.error(
          "[ABFeedTimelineCompact] Failed to load feed views:",
          err,
        );
      } finally {
        // Mark ready even on failure so the feed still loads (unfiltered).
        if (isMounted) setViewsStatus("ready");
      }
    };

    loadFeedViews();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCardClick = useCallback(
    (item) => {
      const cardId = item.document_id;

      setActiveCardId(cardId);
      setRightId?.(cardId);
      setRightSidebarMode?.(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={item}
          cardId={cardId}
          onInteract={() => {}}
          onClose={() => setIsRightOpen(false)}
        />,
      );
    },
    [
      setActiveCardId,
      setIsRightOpen,
      setRightComponent,
      setRightId,
      setRightSidebarMode,
    ],
  );

  // Bumped on every request; responses from older requests are ignored so
  // rapid chip toggles can't land out of order.
  const requestIdRef = useRef(0);

  // document_ids already on the timeline
  const seenIdsRef = useRef(new Set());

  const fetchPage = useCallback(
    async (nextOffset = 0, append = false) => {
      const requestId = ++requestIdRef.current;
      const isLatest = () => requestId === requestIdRef.current;

      const targetTypes = fetchTargetTypesKey
        ? fetchTargetTypesKey.split(",")
        : [];

      // Chips exist but all are off: nothing to ask the API for.
      if (hasFilterViews && !targetTypes.length) {
        setItems([]);
        setOffset(0);
        setHasMore(false);
        setError("");
        setIsLoadingMore(false);
        setLoadMoreFailed(false);
        setStatus("ready");
        return;
      }

      if (append) setIsLoadingMore(true);
      else setStatus("loading");

      setError("");
      setLoadMoreFailed(false);

      try {
        const payload = buildPayload({
          offset: nextOffset,
          target: currentCompany?.target,
          targetTypes,
        });

        console.log("[ABFeedTimelineCompact] getFeed", payload);

        const response = await getFeed(payload);
        if (!isLatest()) return;

        const data = Array.isArray(response?.data) ? response.data : [];

        // New items landing between requests shift the pages, so the next
        // page can repeat ones already shown.
        if (!append) seenIdsRef.current = new Set();
        const fresh = data.filter(
          (item) => !seenIdsRef.current.has(item.document_id),
        );
        fresh.forEach((item) => seenIdsRef.current.add(item.document_id));

        setItems((prev) => (append ? [...prev, ...fresh] : fresh));
        setOffset(nextOffset);
        // Keep paging until a page brings nothing new (empty, or all repeats).
        setHasMore(fresh.length > 0);
        setStatus("ready");
      } catch (err) {
        if (!isLatest()) return;

        console.error("[ABFeedTimelineCompact] Failed to load:", err);

        // A failed page keeps what's already on screen and offers a retry.
        if (append) {
          setLoadMoreFailed(true);
        } else {
          setError(err?.message || "Failed to load timeline.");
          setStatus("error");
        }
      } finally {
        if (isLatest()) setIsLoadingMore(false);
      }
    },
    [currentCompany?.target, fetchTargetTypesKey, hasFilterViews],
  );

  // Wait for feed views so the first fetch uses the right target types.
  useEffect(() => {
    if (viewsStatus !== "ready") return;
    fetchPage(0, false);
  }, [fetchPage, viewsStatus]);

  useEffect(() => {
    if (!activeCardId) return;

    const stillVisible = filteredItems.some(
      (item) => item.document_id === activeCardId,
    );

    if (!stillVisible) {
      setActiveCardId(null);
    }
  }, [activeCardId, filteredItems]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    fetchPage(offset + 1, true);
  }, [fetchPage, hasMore, isLoadingMore, offset]);

  const scrollRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleScroll = useCallback((event) => {
    setShowBackToTop(event.currentTarget.scrollTop > BACK_TO_TOP_THRESHOLD);
  }, []);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="relative h-full w-full">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="ab-feed-scroll relative h-full w-full overflow-y-auto bg-mainBg font-poppins text-mainText"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`
        .ab-feed-scroll::-webkit-scrollbar {
          display: none;
        }

        @keyframes ab-source-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px var(--source-pulse-a, rgba(${cyan},0.06)),
              0 0 12px 2px var(--source-pulse-a, rgba(${cyan},0.07)),
              0 10px 26px rgba(0,0,0,0.14);
          }
          50% {
            box-shadow:
              0 0 0 1px var(--source-pulse-b, rgba(${cyan},0.11)),
              0 0 16px 3px var(--source-pulse-b, rgba(${cyan},0.12)),
              0 10px 26px rgba(0,0,0,0.16);
          }
        }
      `}</style>

        {/* Nothing loaded yet: spinner in the middle of the page. While
          scrolling, the sentinel below shows one at the end of the timeline. */}
        {status === "loading" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        <div className="mx-auto w-full max-w-[1440px] px-3 pb-4 pt-3 md:px-5">
          <GradientDefs colors={filterViews.map((view) => view.color)} />
          <SourceFilters
            views={filterViews}
            enabledViews={enabledViews}
            onToggleView={handleToggleView}
            isLoading={viewsStatus === "loading"}
          />

          {status === "error" && (
            <div className="rounded-2xl border border-errorText/25 bg-errorText/10 p-5 text-sm text-errorText">
              {error}
            </div>
          )}

          {status === "ready" && filteredItems.length === 0 && (
            <div
              className="rounded-2xl border bg-surface/60 p-10 text-center text-sm text-mutedText"
              style={{ borderColor: `rgba(${cyan},0.14)` }}
            >
              {filterViews.length > 0 && enabledViewCount === 0
                ? "No sources selected. Turn on a source above to see its items."
                : "No items found for the selected sources."}
            </div>
          )}

          {status === "ready" && filteredItems.length > 0 && (
            <>
              <DesktopTimeline
                items={filteredItems}
                activeCardId={activeCardId}
                onCardClick={handleCardClick}
                getItemView={getItemView}
              />
              <MobileTimeline
                items={filteredItems}
                activeCardId={activeCardId}
                onCardClick={handleCardClick}
                getItemView={getItemView}
              />

              {/* Next page loads as the sentinel scrolls into view. After a
                failed page it gives way to a manual retry, so a bad request
                isn't re-fired in a loop. */}
              {loadMoreFailed ? (
                <div className="flex flex-col items-center gap-2 pb-8 pt-5">
                  <p className="text-xs text-mutedText">
                    Couldn't load more items.
                  </p>
                  <button
                    type="button"
                    onClick={loadMore}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-surfaceBorder bg-innerBg px-4 py-2 text-xs text-mainText transition hover:bg-btnHover"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <ScrollSentinel
                  onIntersect={loadMore}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Floats over the timeline, outside the scroller so it stays put */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        title="Back to top"
        aria-hidden={!showBackToTop}
        tabIndex={showBackToTop ? 0 : -1}
        className={[
          "absolute bottom-5 right-5 z-30 flex h-10 w-10 items-center justify-center rounded-full border bg-mainBg/90 text-mutedText shadow-login-panel backdrop-blur-md",
          "outline-none transition duration-200 hover:text-mainText focus-visible:ring-2 focus-visible:ring-inputFocus/60",
          showBackToTop
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-2 opacity-0",
        ].join(" ")}
        style={{
          borderColor: `rgba(${cyan},0.22)`,
          boxShadow: `0 0 0 1px rgba(${cyan},0.05), 0 0 16px rgba(${cyan},0.08)`,
        }}
      >
        <ArrowUp className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );
};

export default ABFeedTimelineCompact;
